import { type DragEvent, type MouseEvent, type PointerEvent, useEffect, useRef, useState } from "react";
import { SWIPE_SLOP, type SwipeAxis, swipeAxis, swipeDecision } from "./swipe-gesture";

type Session = {
  id: number;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  width: number;
  axis: SwipeAxis;
  element: HTMLElement;
};
export function useCardSwipe({
  disabled,
  cardId,
  onSwipe,
}: {
  disabled: boolean;
  cardId?: string;
  onSwipe(decision: "like" | "pass"): void;
}) {
  const session = useRef<Session | null>(null);
  const suppressClick = useRef(false);
  const frame = useRef<number | null>(null);
  const options = useRef({ disabled, onSwipe });
  options.current = { disabled, onSwipe };
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const clear = useRef(() => {});
  clear.current = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const active = session.current;
    session.current = null;
    if (active?.element.hasPointerCapture(active.id)) active.element.releasePointerCapture(active.id);
    setOffset(0);
    setDragging(false);
  };
  useEffect(() => {
    if (disabled || cardId) clear.current();
  }, [disabled, cardId]);
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );
  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (session.current) {
      if (session.current.id !== event.pointerId) {
        suppressClick.current = true;
        clear.current();
      }
      return;
    }
    suppressClick.current = false;
    if (options.current.disabled || !event.isPrimary || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest("button,a,input,textarea,select,video,[contenteditable=true],[data-no-swipe]");
    if (control && !control.hasAttribute("data-photo-navigation")) return;
    session.current = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dx: 0,
      dy: 0,
      width: event.currentTarget.clientWidth,
      axis: "pending",
      element: event.currentTarget,
    };
  };
  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const active = session.current;
    if (!active || active.id !== event.pointerId) return;
    if (options.current.disabled || (event.pointerType === "mouse" && event.buttons === 0)) {
      clear.current();
      return;
    }
    active.dx = event.clientX - active.startX;
    active.dy = event.clientY - active.startY;
    if (Math.max(Math.abs(active.dx), Math.abs(active.dy)) >= SWIPE_SLOP) suppressClick.current = true;
    active.axis = swipeAxis(active.axis, active.dx, active.dy);
    if (active.axis !== "horizontal") return;
    if (!active.element.hasPointerCapture(active.id)) active.element.setPointerCapture(active.id);
    setDragging(true);
    if (frame.current === null)
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        if (session.current === active)
          setOffset(Math.max(-active.width * 0.75, Math.min(active.width * 0.75, active.dx)));
      });
  };
  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const active = session.current;
    if (!active || active.id !== event.pointerId) return;
    const decision = swipeDecision(
      active.axis,
      event.clientX - active.startX,
      event.clientY - active.startY,
      active.width,
    );
    clear.current();
    if (decision && !options.current.disabled) options.current.onSwipe(decision);
  };
  const cancel = (event: PointerEvent<HTMLElement>) => {
    if (session.current?.id !== event.pointerId) return;
    suppressClick.current = true;
    clear.current();
  };
  const onClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (event.detail !== 0 && suppressClick.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressClick.current = false;
    }
  };
  return {
    offset,
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: cancel,
      onLostPointerCapture: (event: PointerEvent<HTMLElement>) => {
        if (event.target === event.currentTarget) cancel(event);
      },
      onClickCapture,
      onDragStart: (event: DragEvent<HTMLElement>) => {
        if (session.current) event.preventDefault();
      },
    },
  };
}
